import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { forceDeleteCookie } from '../utils/cookie.utils';

export interface ClaveUnicaUserInfo {
  sub: string;
  RolUnico: {
    DV: string;
    numero: number;
    tipo: string;
  };
  name: {
    apellidos: string[];
    nombres: string[];
  };
}

export interface ClaveUnicaHealthResponse {
  status: string;
  configured: boolean;
  message?: string;
}

export interface ClaveUnicaLoginResponse {
  url: string;
  logoutUrl: string;
}

@Injectable({
  providedIn: 'root',
})
export class ClaveUnicaService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);

  // Constantes para SessionStorage
  readonly SESSION_STORAGE_KEYS = {
    TOKEN: 'claveunica_token',
    USER_INFO: 'claveunica_user_info',
    RETURN_URL: 'claveunica_return_url',
    URL_RETORNO: 'claveunica_url_retorno',
    TRANSACCION_ID: 'claveunica_transaccion_id',
    CODE: 'claveunica_code',
    STATE: 'claveunica_state',
    ERROR_URL: 'claveunica_error_url',
  } as const;

  // Constantes para cookies
  private readonly COOKIE_NAMES = {
    TOKEN: 'clave_unica_token',
  } as const;

  // URL de logout de ClaveÚnica
  private readonly CLAVE_UNICA_LOGOUT_URL =
    'https://accounts.claveunica.gob.cl/api/v1/accounts/app/logout';

  // Endpoints del BFF (Backend For Frontend)
  private readonly BFF_ENDPOINTS = {
    health: `${environment.baseUrl}/clave-unica/health`,
    info: `${environment.baseUrl}/clave-unica/info`,
    login: `${environment.baseUrl}/clave-unica/login`,
    callback: `${environment.baseUrl}/clave-unica/callback`,
    user: `${environment.baseUrl}/clave-unica/user`,
    logout: `${environment.baseUrl}/clave-unica/logout`,
  };

  /**
   * Verifica si ClaveÚnica está configurado en el backend
   */
  public async checkHealth(): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.get<ClaveUnicaHealthResponse>(this.BFF_ENDPOINTS.health),
      );
      return response.configured;
    } catch (error) {
      console.error('Error verificando configuración de ClaveÚnica:', error);
      return false;
    }
  }

  /**
   * Verifica si ClaveÚnica está configurado
   * Esta función es sincrónica para compatibilidad con el componente del botón
   */
  public isConfigured(): boolean {
    // Retorna true si el baseUrl está configurado
    // El check real se hace en checkHealth() de forma asincrónica
    return !!environment.baseUrl;
  }

  /**
   * Inicia el proceso de autenticación con ClaveÚnica
   * Envía solicitud al BFF con token JWT en header Authorization Bearer
   * @param tokenCU Token JWT para autenticación
   */
  public async iniciarAutenticacion(tokenCU?: string): Promise<void> {
    this.limpiarSesion();
    this.guardarTokenEnSesion(tokenCU);

    const headers = this.buildAuthHeaders(tokenCU);
    const loginUrl = this.buildLoginUrl();

    const response = await this.executeLoginRequest(loginUrl, headers);
    console.warn('ClaveUnica login response:', response);
    this.handleLoginResponse(response);
  }

  /**
   * Guarda el token en sessionStorage si existe
   */
  private guardarTokenEnSesion(tokenCU?: string): void {
    if (tokenCU) {
      sessionStorage.setItem(this.SESSION_STORAGE_KEYS.TOKEN, tokenCU);
    }
  }

  /**
   * Construye los headers para la petición de autenticación
   * NOTA: NO agregamos headers de cache porque:
   * 1. Ya usamos timestamp en la URL (?_t=...) para evitar cache
   * 2. Headers personalizados causan preflight OPTIONS que pueden fallar con CORS
   */
  private buildAuthHeaders(tokenCU?: string): HttpHeaders {
    let headers = new HttpHeaders();

    if (tokenCU) {
      headers = headers.set('Authorization', `Bearer ${tokenCU}`);
    }

    return headers;
  }

  /**
   * Construye la URL de login con timestamp para evitar cache
   */
  private buildLoginUrl(): string {
    const timestamp = Date.now();
    return `${this.BFF_ENDPOINTS.login}?_t=${timestamp}`;
  }

  /**
   * Ejecuta la petición de login al BFF
   */
  private async executeLoginRequest(
    loginUrl: string,
    headers: HttpHeaders,
  ): Promise<ClaveUnicaLoginResponse> {
    try {
      return await firstValueFrom(
        this.http.get<ClaveUnicaLoginResponse>(loginUrl, { headers }),
      );
    } catch (error) {
      console.error('Error iniciando autenticación ClaveÚnica:', error);
      throw error;
    }
  }

  /**
   * Procesa la respuesta del login y redirige al usuario
   */
  private handleLoginResponse(response: ClaveUnicaLoginResponse): never {
    if (!response.url) {
      throw new Error('No se recibió URL de redirección del backend');
    }
    this.navigateToExternalUrl(response.url);
  }

  /**
   * Navega a una URL externa de forma segura y testeable
   * Fuerza recarga completa para evitar problemas de cache
   * @param url URL externa a la que redirigir
   */
  private navigateToExternalUrl(url: string): never {
    window.location.replace(url);
    throw new Error('Navigation in progress');
  }

  /**
   * Obtiene la información del usuario autenticado
   * Llama al BFF que retorna los datos del usuario en sesión
   */
  public async obtenerUsuario(): Promise<ClaveUnicaUserInfo | null> {
    try {
      const userInfo = await firstValueFrom(
        this.http.get<ClaveUnicaUserInfo>(this.BFF_ENDPOINTS.user),
      );
      return userInfo;
    } catch (error) {
      console.error('Error obteniendo usuario de ClaveÚnica:', error);
      return null;
    }
  }

  /**
   * Guarda la información del usuario en sessionStorage
   */
  public guardarUserInfo(userInfo: ClaveUnicaUserInfo): void {
    if (!userInfo) {
      console.warn('Se intentó guardar userInfo nulo o indefinido');
      return;
    }
    sessionStorage.setItem(
      this.SESSION_STORAGE_KEYS.USER_INFO,
      JSON.stringify(userInfo),
    );
  }

  /**
   * Obtiene la información del usuario guardada en sessionStorage
   */
  public getUserInfo(): ClaveUnicaUserInfo | null {
    const data = sessionStorage.getItem(this.SESSION_STORAGE_KEYS.USER_INFO);
    if (!data) {
      return null;
    }

    try {
      return JSON.parse(data) as ClaveUnicaUserInfo;
    } catch (error) {
      console.error('Error parseando userInfo de sessionStorage:', error);
      return null;
    }
  }

  /**
   * Obtiene la URL de retorno guardada en sessionStorage
   */
  public getUrlRetorno(): string | null {
    return sessionStorage.getItem(this.SESSION_STORAGE_KEYS.URL_RETORNO);
  }

  /**
   * Obtiene la URL de retorno del callback guardada en sessionStorage
   */
  public getReturnUrl(): string | null {
    return sessionStorage.getItem(this.SESSION_STORAGE_KEYS.RETURN_URL);
  }

  /**
   * Obtiene el ID de transacción guardado en sessionStorage
   */
  public getTransaccionId(): string | null {
    return sessionStorage.getItem(this.SESSION_STORAGE_KEYS.TRANSACCION_ID);
  }

  /**
   * Limpia los datos de sesión de ClaveÚnica
   */
  public limpiarSesion(): void {
    this.clearClaveUnicaCookies();
  }

  /**
   * Elimina todas las cookies relacionadas con ClaveÚnica
   */
  private clearClaveUnicaCookies(): void {
    const cookieNames = Object.values(this.COOKIE_NAMES);
    cookieNames.forEach((cookieName) => forceDeleteCookie(cookieName));
  }

  /**
   * Cierra la sesión de ClaveÚnica
   * Limpia los datos locales y redirige al home
   */
  public async cerrarSesion(): Promise<void> {
    this.limpiarSesion();
    await this.router.navigate(['/claveunica/error']);
  }

  /**
   * Procesa el callback de ClaveÚnica después de la autenticación
   * Los errores se propagan al componente que llama para manejo personalizado
   * @param code Código de autorización recibido
   * @param state Estado de la sesión
   * @returns Promise que se resuelve después de cerrar sesión en ClaveÚnica
   */
  public async callback(code: string, state: string): Promise<void> {
    if (!code || !state) {
      throw new Error('Callback sin code o state válidos');
    }

    this.guardarCallbackParams(code, state);
    const response = await this.executeCallbackRequest(code, state);
    console.warn('ClaveUnica callback response:', response);
    await this.handleCallbackResponse(response);

    // Ahora puedes continuar con más lógica aquí en el componente que llama
    // La sesión de ClaveÚnica ya está cerrada en este punto
  }

  /**
   * Guarda los parámetros del callback en sessionStorage
   */
  private guardarCallbackParams(code: string, state: string): void {
    sessionStorage.setItem(this.SESSION_STORAGE_KEYS.CODE, code);
    sessionStorage.setItem(this.SESSION_STORAGE_KEYS.STATE, state);
  }

  /**
   * Ejecuta la petición al endpoint de callback del BFF
   */
  private async executeCallbackRequest(
    code: string,
    state: string,
  ): Promise<ClaveUnicaLoginResponse> {
    const callbackUrl = `${this.BFF_ENDPOINTS.callback}?code=${code}&state=${state}`;
    return await firstValueFrom(
      this.http.get<ClaveUnicaLoginResponse>(callbackUrl),
    );
  }

  /**
   * Procesa la respuesta del callback y cierra sesión en ClaveÚnica
   * response.url contiene la URL de redirección final (no la de logout)
   */
  private async handleCallbackResponse(
    response: ClaveUnicaLoginResponse,
  ): Promise<void> {
    if (!response.url) {
      throw new Error('No se recibió URL de redirección del backend');
    }
    // Limpiar sesión local
    this.limpiarSesion();

    sessionStorage.setItem(this.SESSION_STORAGE_KEYS.URL_RETORNO, response.url);

    window.location.href = response.logoutUrl;
    // Esperar un momento breve para dar tiempo al logout
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Redirigir inmediatamente a la URL final
    console.warn('[DEBUG] Redirigiendo a URL final:', response.url);
    window.location.href = response.url;
  }
}
